import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import {useDispatch} from 'react-redux';
import moment from 'moment';
import {useAgentsInvoicesUIContext} from '../AgentsIncomesUIContext';
import {FilterFromDate, FilterToDate} from '../../../_common/components/FilterDateRange';
import FilterTextField from '../../../_common/components/FilterTextField';
import FilterCheckbox from '../../../_common/components/FilterCheckbox';
import ButtonExportToExcel from '../../../_common/components/ButtonExportToExcel';
import * as actions from '../../_redux/agentsIncomesActions';
import {generateExcelFile} from '../../../_common/utils/excel';
import {agentsIncomesHeader} from '../../../_common/constants/exportedFilesMainHeaders';
import {formatPhoneNumberForExcel} from '../../../../../utils';
import {headersAgentsIncomes} from '../../../_common/constants/exportedFilesHeaders';
import {RequestCancellationManager} from '../../../_common/utils/RequestCancellationManager';
import FiltersBar from '../../../_common/components/FiltersBar';

const prepareFilter = (queryParams, values) => {
  const {agentID, name, noInvoicesOnly, fromDate, toDate} = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(agentID)) {
    filter.agentID = agentID;
  } else {
    delete newQueryParams.agentID;
  }
  if (Boolean(name)) {
    filter.name = name;
  } else {
    delete newQueryParams.name;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }
  if (Boolean(noInvoicesOnly)) {
    filter.noInvoicesOnly = noInvoicesOnly;
  } else {
    delete newQueryParams.noInvoicesOnly;
  }
  return {...newQueryParams, ...filter};
};

export function AgentsIncomesFilter() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const agentsIncomesUIContext = useAgentsInvoicesUIContext();
  const agentsIncomesUIProps = useMemo(() => {
    return {
      queryParams: agentsIncomesUIContext.queryParams,
      setQueryParams: agentsIncomesUIContext.setQueryParams,
    };
  }, [agentsIncomesUIContext]);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(actions.getAgentsIncomesListIDPrefix);

    const newQueryParams = prepareFilter(agentsIncomesUIProps.queryParams, values);
    if (!isEqual(newQueryParams, agentsIncomesUIProps.queryParams)) {
      agentsIncomesUIProps.setQueryParams(newQueryParams);
    }
  };

  const qs = new URLSearchParams(window.location.search);
  const agentID = qs.get('agentID') || '';
  const name = qs.get('name') || '';
  const noInvoicesOnly = qs.get('noInvoicesOnly') || '';
  const fromDate = qs.get('fromDate') || moment().month(0).date(1).toDate();
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : moment().toDate();

  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      return {
        [headersAgentsIncomes[0]]: index + 1,
        [headersAgentsIncomes[1]]: item.fullName || '',
        [headersAgentsIncomes[2]]: item.agentId || '',
        [headersAgentsIncomes[3]]: item.email || '',
        [headersAgentsIncomes[4]]: item.phone ? formatPhoneNumberForExcel(item.phone) : '',
        [headersAgentsIncomes[5]]: item.totalWithVAT || '',
        [headersAgentsIncomes[6]]: (item.lastInvoiceDate) ? moment(item.lastInvoiceDate).format('DD.MM.YYYY') : '',
      };
    });
  };
  const exportUsersDataFileFunc = () => {
    dispatch(actions.exportAgentsIncomesFile(agentsIncomesUIContext.queryParams))
      .then(prepareDataForExcel)
      .then((data) => generateExcelFile(data, 'Agents incomes data.xlsx', agentsIncomesHeader))
      .catch((error) => {
        console.error(`Error: + ${error}`);
      });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <Formik
      initialValues={{
        agentID,
        name,
        noInvoicesOnly,
        fromDate,
        toDate,
      }}
      onSubmit={(values) => applyFilter(values)}
      enableReinitialize={true}
    >
      {({values, ...formikProps}) => (
        <FiltersBar
          filters={
            <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
              <FilterTextField
                fieldName="agentID"
                placeholder={intl.formatMessage({id: 'agents-invoices-filter-agentID', defaultMessage: 'Agent Id'})}
                value={values.agentID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="name"
                placeholder={intl.formatMessage({id: 'agents-invoices-filter-name', defaultMessage: 'Customer Id'})}
                value={values.name}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterFromDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterToDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterCheckbox
                fieldName="noInvoicesOnly"
                label={intl.formatMessage({id: 'agents-invoices-filter-noInvoicesOnly', defaultMessage: 'No invoices only'})}
                value={values.noInvoicesOnly}
                containerStyles={containerClassnames}
                {...formikProps}
              />
            </div>
          }
          buttons={<ButtonExportToExcel exportFunc={exportUsersDataFileFunc}/>}
        />
      )}
    </Formik>
  );
}
